import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Link,
  Modal,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
  Textarea,
  type CardsProps,
  type SelectProps,
  type TableProps,
} from '@cloudscape-design/components';
import {
  fetchDeployments,
  createDeployment,
  updateDeployment,
  getLaunchUrl,
  validateDeployment as apiValidateDeployment,
  removeDeployment as apiRemoveDeployment,
  type RoleDeployment,
  type ProductDefinition,
  type DeploymentStatus,
} from '../api/deployments';
import { fetchTenants } from '../api/tenants';
import type { Tenant } from '../types/tenant';

const STATUS_MAP: Record<DeploymentStatus, { type: 'pending' | 'in-progress' | 'success' | 'error' | 'stopped'; label: string }> = {
  pending: { type: 'pending', label: 'Pending' },
  parameters_set: { type: 'in-progress', label: 'Ready to Deploy' },
  cfn_launched: { type: 'in-progress', label: 'Stack Launched' },
  deployed: { type: 'success', label: 'Deployed' },
  integrated: { type: 'success', label: 'Integrated' },
  validation_failed: { type: 'error', label: 'Validation Failed' },
  failed: { type: 'error', label: 'Failed' },
  removed: { type: 'stopped', label: 'Removed' },
};

export function RoleDeployments() {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<SelectProps.Option | null>(null);
  const [deployments, setDeployments] = useState<RoleDeployment[]>([]);
  const [catalog, setCatalog] = useState<Record<string, ProductDefinition>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addProductId, setAddProductId] = useState<SelectProps.Option | null>(null);
  const [addParams, setAddParams] = useState<Record<string, string>>({});
  const [addNotes, setAddNotes] = useState('');
  const [addTargetAccount, setAddTargetAccount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editDeployment, setEditDeployment] = useState<RoleDeployment | null>(null);
  const [editParams, setEditParams] = useState<Record<string, string>>({});
  const [editOutputs, setEditOutputs] = useState<Record<string, string>>({});
  const [editNotes, setEditNotes] = useState('');

  const activeTenantId = routeTenantId || selectedTenant?.value || '';

  useEffect(() => {
    fetchTenants().then((res) => {
      setTenants(res.tenants);
      if (routeTenantId) {
        const t = res.tenants.find((t) => t.tenantId === routeTenantId);
        if (t) setSelectedTenant({ label: `${t.name} (${t.companyName})`, value: t.tenantId });
      }
    }).catch(() => {});
  }, [routeTenantId]);

  useEffect(() => {
    if (!activeTenantId) return;
    loadDeployments();
  }, [activeTenantId]);

  function loadDeployments() {
    setLoading(true);
    setError(null);
    fetchDeployments(activeTenantId)
      .then((res) => { setDeployments(res.deployments); setCatalog(res.catalog); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  const tenantOptions: SelectProps.Options = tenants.map((t) => ({
    label: `${t.name} — ${t.companyName}`,
    value: t.tenantId,
    description: `${t.awsAccountId || 'No AWS account'} · ${t.status}`,
  }));

  const availableProducts = Object.values(catalog).filter(
    (p) => !deployments.some((d) => d.productId === p.productId)
  );
  const productOptions: SelectProps.Options = availableProducts.map((p) => ({
    label: p.productName, value: p.productId, description: p.description,
  }));
  const selectedProductDef = addProductId?.value ? catalog[addProductId.value] : null;

  // ── Handlers ──
  async function handleCreate() {
    if (!activeTenantId || !addProductId?.value) return;
    setSubmitting(true);
    try {
      const params = { ...addParams };
      if (addTargetAccount) params['CustomerAccountId'] = addTargetAccount;
      const dep = await createDeployment(activeTenantId, addProductId.value, params, addNotes || undefined);
      setDeployments((prev) => [...prev, dep]);
      setShowAddModal(false);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to create'); }
    finally { setSubmitting(false); }
  }

  async function handleUpdate() {
    if (!editDeployment) return;
    setSubmitting(true);
    try {
      const cleanOutputs: Record<string, string> = {};
      for (const [k, v] of Object.entries(editOutputs)) { if (v?.trim()) cleanOutputs[k] = v.trim(); }
      const updated = await updateDeployment(activeTenantId, editDeployment.productId, {
        parameters: editParams,
        stackOutputs: Object.keys(cleanOutputs).length > 0 ? cleanOutputs : undefined,
        notes: editNotes || undefined,
      });
      setDeployments((prev) => prev.map((d) => (d.productId === updated.productId ? updated : d)));
      setEditDeployment(null);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to update'); }
    finally { setSubmitting(false); }
  }

  async function handleLaunchStack(dep: RoleDeployment) {
    try {
      const result = await getLaunchUrl(activeTenantId, dep.productId);
      if (result.launchUrl) {
        window.open(result.launchUrl, '_blank');
        const updated = await updateDeployment(activeTenantId, dep.productId, { status: 'cfn_launched' });
        setDeployments((prev) => prev.map((d) => (d.productId === updated.productId ? updated : d)));
      } else { setError('No template available.'); }
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
  }

  async function handleMarkDeployed(dep: RoleDeployment) {
    try {
      const updated = await updateDeployment(activeTenantId, dep.productId, { status: 'deployed' });
      setDeployments((prev) => prev.map((d) => (d.productId === updated.productId ? updated : d)));
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed'); }
  }

  async function handleValidate(dep: RoleDeployment) {
    const acct = dep.stackOutputs?.customerAccountId || dep.parameters?.CustomerAccountId || tenants.find((t) => t.tenantId === activeTenantId)?.awsAccountId || '';
    if (!acct || !/^\d{12}$/.test(acct)) {
      const input = prompt(`12-digit AWS Account ID where ${dep.productName} role was deployed:`, acct);
      if (!input || !/^\d{12}$/.test(input.trim())) { setError('Valid 12-digit account ID required'); return; }
      try {
        const result = await apiValidateDeployment(activeTenantId, dep.productId, input.trim());
        handleValidateResult(dep, result, input.trim());
      } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Validation failed'); }
      return;
    }
    // Auto-validate with known account ID — no prompt needed
    try {
      const result = await apiValidateDeployment(activeTenantId, dep.productId, acct);
      handleValidateResult(dep, result, acct);
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Validation failed'); }
  }

  function handleValidateResult(dep: RoleDeployment, result: { validated: boolean; customerAccountId: string; roleArn: string; message: string }, acct: string) {
    setDeployments((prev) => prev.map((d) =>
      d.productId === dep.productId
        ? { ...d, status: (result.validated ? 'integrated' : 'validation_failed') as DeploymentStatus,
            validated: result.validated, validatedAt: new Date().toISOString(),
            stackOutputs: { ...d.stackOutputs, customerAccountId: acct, roleArn: result.roleArn } }
        : d
    ));
    if (!result.validated) setError(result.message);
    else setError(null);
  }

  async function handleRemove(dep: RoleDeployment) {
    if (!confirm(`Remove ${dep.productName}? This action is audited.`)) return;
    try {
      await apiRemoveDeployment(activeTenantId, dep.productId);
      setDeployments((prev) => prev.filter((d) => d.productId !== dep.productId));
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to remove'); }
  }

  async function handleUpdateRole(dep: RoleDeployment) {
    const acct = dep.stackOutputs?.customerAccountId || tenants.find((t) => t.tenantId === activeTenantId)?.awsAccountId || '';
    if (!acct || !/^\d{12}$/.test(acct)) {
      const input = prompt('12-digit AWS Account ID for role update:', acct);
      if (!input || !/^\d{12}$/.test(input.trim())) { setError('Valid 12-digit account ID required'); return; }
    }
    const customerAccountId = acct;
    try {
      setError(null);
      const resp = await fetch(
        `${import.meta.env.VITE_OPS_API_URL || 'https://k6wq9xbvc8.execute-api.us-east-1.amazonaws.com/dev'}/ops/products/${dep.productId}/versions/latest/deploy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tenantId: activeTenantId, customerAccountId }),
        }
      );
      const result = await resp.json();
      if (!resp.ok) throw new Error(result?.error?.message || 'Update failed');
      alert(`Role stack update initiated: ${result.message || 'success'}`);
      loadDeployments();
    } catch (err: unknown) { setError(err instanceof Error ? err.message : 'Failed to update role'); }
  }

  function openEdit(dep: RoleDeployment) {
    setEditDeployment(dep);
    setEditParams({ ...dep.parameters });
    setEditOutputs({ ...dep.stackOutputs });
    setEditNotes(dep.notes || '');
  }

  // ── Card Definition ──
  const cardDef: CardsProps.CardDefinition<RoleDeployment> = {
    header: (item) => {
      const s = STATUS_MAP[item.status];
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <SpaceBetween direction="horizontal" size="xs">
            <span style={{ fontWeight: 700 }}>{item.productName}</span>
            <Badge color={item.vendor === 'gdna' ? 'blue' : 'grey'}>{item.vendor === 'gdna' ? 'GDNA' : '3rd Party'}</Badge>
          </SpaceBetween>
          <SpaceBetween direction="horizontal" size="xs">
            <StatusIndicator type={s.type}>{s.label}</StatusIndicator>
            {item.validated && <Badge color="green">✓</Badge>}
          </SpaceBetween>
        </div>
      );
    },
    sections: [
      {
        id: 'details',
        content: (item) => {
          const acctId = item.stackOutputs?.customerAccountId;
          const roleName = item.stackOutputs?.roleName;
          const roleArn = item.stackOutputs?.roleArn;
          const productDef = catalog[item.productId];
          const autoParams = new Set((productDef?.requiredParameters || []).filter((p) => p.autoGenerated).map((p) => p.name));
          const manualParams = Object.entries(item.parameters).filter(([key]) => !autoParams.has(key));

          return (
            <ColumnLayout columns={2} variant="text-grid">
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label" fontSize="body-s">Target Account</Box>
                <Box>{acctId ? <code>{acctId}</code> : (item.parameters?.CustomerAccountId ? <code>{item.parameters.CustomerAccountId}</code> : <span style={{ color: '#687078' }}>—</span>)}</Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label" fontSize="body-s">Account Type</Box>
                <Box fontSize="body-s">{(item.productId === 'archera' || item.productId === 'one-ct-wafr') ? '🏦 Org Payer' : '📦 APN/MP'}</Box>
              </SpaceBetween>
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label" fontSize="body-s">Role</Box>
                <Box>{roleName || (roleArn ? <code style={{ fontSize: 11 }}>{roleArn}</code> : <span style={{ color: '#687078' }}>—</span>)}</Box>
              </SpaceBetween>
              {manualParams.map(([key, val]) => (
                <SpaceBetween key={key} size="xxs">
                  <Box variant="awsui-key-label" fontSize="body-s">{key}</Box>
                  <Box><code style={{ fontSize: 12 }}>{val}</code></Box>
                </SpaceBetween>
              ))}
              {manualParams.length === 0 && (
                <SpaceBetween size="xxs">
                  <Box variant="awsui-key-label" fontSize="body-s">Config</Box>
                  <Box fontSize="body-s" color="text-status-inactive">Auto-configured</Box>
                </SpaceBetween>
              )}
            </ColumnLayout>
          );
        },
      },
      {
        id: 'actions',
        content: (item) => {
          const productDef = catalog[item.productId];
          const isPre = item.status === 'parameters_set' || item.status === 'pending';
          const isLaunched = item.status === 'cfn_launched';
          const needsValidation = item.status === 'deployed' || item.status === 'validation_failed';
          const isDone = item.status === 'integrated';

          return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #e9ebed' }}>
              <SpaceBetween direction="horizontal" size="xs">
                {isPre && <Button variant="primary" onClick={() => handleLaunchStack(item)}>Launch Stack</Button>}
                {isLaunched && <Button variant="primary" onClick={() => handleMarkDeployed(item)}>Confirm Deployed</Button>}
                {needsValidation && <Button variant="primary" onClick={() => handleValidate(item)}>Validate</Button>}
                {isDone && <Badge color="green">Complete</Badge>}
                {isDone && <Button onClick={() => handleUpdateRole(item)}>Update Role</Button>}
              </SpaceBetween>
              <ButtonDropdown
                items={[
                  ...(productDef?.externalUrl ? [{ id: 'external', text: `Open ${productDef.productName}`, href: productDef.externalUrl, external: true }] : []),
                  ...(!isDone ? [{ id: 'relaunch', text: 'Relaunch Stack' }] : []),
                  ...(isDone ? [{ id: 'update-role', text: 'Update Role Stack' }] : []),
                  { id: 'edit', text: 'Edit' },
                  { id: 'remove', text: 'Remove' },
                ]}
                onItemClick={({ detail }) => {
                  if (detail.id === 'edit') openEdit(item);
                  if (detail.id === 'remove') handleRemove(item);
                  if (detail.id === 'relaunch') handleLaunchStack(item);
                  if (detail.id === 'update-role') handleUpdateRole(item);
                }}
                variant="icon"
              />
            </div>
          );
        },
      },
    ],
  };

  // ── Overview table: all tenants with role status dots ──
  const [allDeploymentsByTenant, setAllDeploymentsByTenant] = useState<Record<string, RoleDeployment[]>>({});
  const [overviewLoading, setOverviewLoading] = useState(false);

  useEffect(() => {
    if (routeTenantId) return; // skip overview load when in detail mode
    setOverviewLoading(true);
    fetchTenants().then(async (res) => {
      setTenants(res.tenants);
      const byTenant: Record<string, RoleDeployment[]> = {};
      await Promise.all(res.tenants.map(async (t) => {
        try {
          const depRes = await fetchDeployments(t.tenantId);
          byTenant[t.tenantId] = depRes.deployments;
          if (!catalog || Object.keys(catalog).length === 0) setCatalog(depRes.catalog);
        } catch { byTenant[t.tenantId] = []; }
      }));
      setAllDeploymentsByTenant(byTenant);
    }).finally(() => setOverviewLoading(false));
  }, [routeTenantId]);

  function statusDot(status?: string) {
    if (!status) return <span title="Not deployed" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#d1d5db' }} />;
    const colors: Record<string, string> = {
      integrated: '#22c55e', deployed: '#22c55e', parameters_set: '#facc15',
      cfn_launched: '#3b82f6', pending: '#d1d5db', validation_failed: '#ef4444', failed: '#ef4444',
    };
    return <span title={status} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: colors[status] || '#d1d5db' }} />;
  }

  const productIds = Object.keys(catalog);

  const overviewColumns: TableProps.ColumnDefinition<Tenant>[] = [
    { id: 'name', header: 'Tenant', cell: (item) => item.name, width: 150 },
    { id: 'company', header: 'Company', cell: (item) => item.companyName, width: 150 },
    { id: 'account', header: 'AWS Account', cell: (item) => item.awsAccountId ? <code style={{ fontSize: 12 }}>{item.awsAccountId}</code> : '—', width: 130 },
    ...productIds.map((pid) => ({
      id: pid,
      header: catalog[pid]?.productName || pid,
      width: 140,
      cell: (item: Tenant) => {
        const deps = allDeploymentsByTenant[item.tenantId] || [];
        const dep = deps.find((d) => d.productId === pid);
        return (
          <SpaceBetween direction="horizontal" size="xxs">
            {statusDot(dep?.status)}
            <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{dep ? STATUS_MAP[dep.status]?.label || dep.status : '—'}</span>
          </SpaceBetween>
        );
      },
    })),
    {
      id: 'actions', header: '', width: 90,
      cell: (item) => <Button variant="inline-link" onClick={() => navigate(`/ops/deployments/${item.tenantId}`)}>Manage →</Button>,
    },
  ];

  // ── Render ──
  if (!routeTenantId) {
    return (
      <SpaceBetween size="l">
        <Header variant="h1" description="Cross-account trust role status across all tenants. Click a tenant to manage."
          actions={<Button iconName="refresh" onClick={() => window.location.reload()}>Refresh</Button>}
        >Role Deployments</Header>
        <Table
          columnDefinitions={overviewColumns}
          items={tenants}
          loading={overviewLoading}
          loadingText="Loading tenants and deployments..."
          trackBy="tenantId"
          variant="full-page"
          stickyHeader
          stripedRows
          onRowClick={({ detail }) => navigate(`/ops/deployments/${detail.item.tenantId}`)}
          empty={<Box textAlign="center" padding="l">No tenants. Create one from Fulfillment first.</Box>}
          header={<Header counter={`(${tenants.length})`}>All Tenants</Header>}
        />
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup items={[
        { text: 'Role Deployments', href: '/ops/deployments' },
        { text: selectedTenant?.label || routeTenantId || '', href: '#' },
      ]} onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }} />

      <Header variant="h1" description="Deploy cross-account trust roles for GDNA products and partner integrations."
        actions={<SpaceBetween direction="horizontal" size="xs">
          <Button onClick={loadDeployments}>Refresh</Button>
          <Button variant="primary" onClick={() => { setAddProductId(null); setAddParams({}); setAddNotes(''); setAddTargetAccount(''); setShowAddModal(true); }} disabled={!activeTenantId || availableProducts.length === 0}>Add Role Deployment</Button>
          {deployments.some((d) => d.status === 'integrated' || d.status === 'deployed') && (
            <Button variant="primary" iconName="upload" onClick={() => navigate(`/ops/product-deployments?tenant=${activeTenantId}`)}>Deploy Product →</Button>
          )}
        </SpaceBetween>}
      >Role Deployments</Header>

      <Container>
        <FormField label="Select Tenant">
          <Select selectedOption={selectedTenant} onChange={({ detail }) => { setSelectedTenant(detail.selectedOption); if (detail.selectedOption.value) navigate(`/ops/deployments/${detail.selectedOption.value}`); }} options={tenantOptions} placeholder="Select a tenant..." filteringType="auto" />
        </FormField>
      </Container>

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      {loading ? <Box textAlign="center" padding="l">Loading...</Box> : deployments.length === 0 ? (
            <Box textAlign="center" padding="l" color="text-status-inactive">No deployments yet. Click "Add Role Deployment" to get started.</Box>
          ) : (
            <ColumnLayout columns={deployments.length >= 3 ? 3 : deployments.length} variant="default">
              {deployments.map((dep) => {
                const card = cardDef;
                return (
                  <Container key={dep.productId} variant="stacked">
                    {card.header?.(dep)}
                    <Box padding={{ top: 's' }}>{card.sections?.[0]?.content?.(dep)}</Box>
                    {card.sections?.[1]?.content?.(dep)}
                  </Container>
                );
              })}
            </ColumnLayout>
          )}

      {/* Add Modal */}
      <Modal visible={showAddModal} onDismiss={() => setShowAddModal(false)} header="Add Role Deployment"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate} loading={submitting} disabled={!addProductId}>Create</Button>
        </SpaceBetween></Box>}>
        <SpaceBetween size="m">
          <FormField label="Product"><Select selectedOption={addProductId} onChange={({ detail }) => {
            setAddProductId(detail.selectedOption);
            setAddParams({});
            // Pre-fill target account based on product type
            const tenant = tenants.find((t) => t.tenantId === activeTenantId);
            const pid = detail.selectedOption.value || '';
            if (pid === 'archera' || pid === 'one-ct-wafr') {
              // These go to org root/payer account
              setAddTargetAccount(tenant?.awsAccountId || '');
            } else {
              // PC3/ACP go to APN/MP account (may be different)
              setAddTargetAccount(tenant?.awsAccountId || '');
            }
          }} options={productOptions} placeholder="Choose a product..." /></FormField>
          {selectedProductDef && (<>
            <Alert type="info">{selectedProductDef.description}</Alert>
            {(addProductId?.value === 'archera' || addProductId?.value === 'one-ct-wafr') && (
              <Alert type="warning">This role must be deployed to the <strong>org root/payer account</strong> — it needs org-level cost and billing data.</Alert>
            )}
            {(addProductId?.value === 'aeos-pc3' || addProductId?.value === 'acp') && (
              <Alert type="info">This role should be deployed to the <strong>APN/Marketplace account</strong> — separate from the org payer if possible.</Alert>
            )}
            <FormField label="Target AWS Account ID" description="12-digit account ID where this role will be deployed" constraintText={
              (addProductId?.value === 'archera' || addProductId?.value === 'one-ct-wafr') ? 'Must be the org root/payer account' : 'Should be the APN/MP member account'
            }>
              <Input value={addTargetAccount} onChange={({ detail }) => setAddTargetAccount(detail.value)} placeholder="123456789012" inputMode="numeric" />
            </FormField>
            <Alert type="info">{selectedProductDef.description}</Alert>
            {selectedProductDef.externalUrl && <Alert type="warning">Create tenant at <Link external href={selectedProductDef.externalUrl}>{selectedProductDef.externalUrl}</Link> first, then paste parameters below.</Alert>}
            {selectedProductDef.requiredParameters.filter((p) => !p.autoGenerated).map((param) => (
              <FormField key={param.name} label={param.label} description={param.description} constraintText={param.required ? 'Required' : 'Optional'}>
                <Input value={addParams[param.name] || ''} onChange={({ detail }) => setAddParams((prev) => ({ ...prev, [param.name]: detail.value }))} placeholder={param.type === 'uuid' ? 'c3a157f6-78f2-49f1-a95c-76d3668ebd3a' : param.type === 'arn' ? 'arn:aws:iam::...' : ''} />
              </FormField>
            ))}
            <FormField label="Notes"><Textarea value={addNotes} onChange={({ detail }) => setAddNotes(detail.value)} rows={2} /></FormField>
          </>)}
        </SpaceBetween>
      </Modal>

      {/* Edit Modal */}
      <Modal visible={editDeployment !== null} onDismiss={() => setEditDeployment(null)} header={`Edit: ${editDeployment?.productName || ''}`} size="large"
        footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => setEditDeployment(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleUpdate} loading={submitting}>Save</Button>
        </SpaceBetween></Box>}>
        {editDeployment && (
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <div><Box variant="awsui-key-label">Product</Box><Box>{editDeployment.productName}</Box></div>
              <div><Box variant="awsui-key-label">Status</Box><StatusIndicator type={STATUS_MAP[editDeployment.status].type}>{STATUS_MAP[editDeployment.status].label}</StatusIndicator></div>
            </ColumnLayout>
            {catalog[editDeployment.productId]?.requiredParameters.filter((p) => !p.autoGenerated).map((param) => (
              <FormField key={param.name} label={param.label} description={param.description}>
                <Input value={editParams[param.name] || ''} onChange={({ detail }) => setEditParams((prev) => ({ ...prev, [param.name]: detail.value }))} />
              </FormField>
            ))}
            <FormField label="Customer Account ID"><Input value={editOutputs['customerAccountId'] || ''} onChange={({ detail }) => setEditOutputs((prev) => ({ ...prev, customerAccountId: detail.value }))} placeholder="123456789012" /></FormField>
            <FormField label="Role ARN"><Input value={editOutputs['roleArn'] || ''} onChange={({ detail }) => setEditOutputs((prev) => ({ ...prev, roleArn: detail.value }))} placeholder="arn:aws:iam::...:role/..." /></FormField>
            <FormField label="Notes"><Textarea value={editNotes} onChange={({ detail }) => setEditNotes(detail.value)} rows={2} /></FormField>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
}
